<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import ContactImportColumnMapping from './ContactImportColumnMapping.vue';
import {
  readCsvHead,
  buildMappingRows,
  getMappingIssues,
  toColumnMapping,
} from 'dashboard/helper/contactImportHelper';

const emit = defineEmits(['import']);
const { t } = useI18n();

const SAMPLE_CSV_URL = '/downloads/import-contacts-sample.csv';

const uiFlags = useMapGetter('contacts/getUIFlags');
const isImportingContact = computed(() => uiFlags.value.isImporting);

const allLabels = useMapGetter('labels/getLabels');
const labelOptions = computed(() =>
  allLabels.value.map(label => ({ value: label.title, label: label.title }))
);

const contactAttributes = useMapGetter('attributes/getContactAttributes');
const attributeKeys = computed(() =>
  (contactAttributes.value || []).map(attribute => attribute.attributeKey)
);

const dialogRef = ref(null);
const fileInput = ref(null);

// 'file' picks the CSV and labels; 'mapping' says where each column goes.
const step = ref('file');
const selectedFile = ref(null);
const selectedFileName = ref('');
const selectedLabels = ref([]);
const isReadingFile = ref(false);
const readError = ref('');
const mappingRows = ref([]);
const serverErrors = ref([]);

const isMappingStep = computed(() => step.value === 'mapping');
const mappingIssues = computed(() =>
  getMappingIssues(mappingRows.value, attributeKeys.value)
);
const canImport = computed(
  () =>
    mappingIssues.value.phoneCount === 1 &&
    Object.keys(mappingIssues.value.rowErrors).length === 0
);

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);
  return baseName.length > 20
    ? `${baseName.slice(0, 20)}...${extension}`
    : fileName;
};

const resetMapping = () => {
  step.value = 'file';
  mappingRows.value = [];
  serverErrors.value = [];
  readError.value = '';
};

const handleFileClick = () => fileInput.value?.click();

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  selectedFile.value = file || null;
  selectedFileName.value = file ? processFileName(file.name) : '';
  resetMapping();
};

const handleRemoveFile = () => {
  selectedFile.value = null;
  if (fileInput.value) fileInput.value.value = null;
  selectedFileName.value = '';
  resetMapping();
};

const openMapping = async () => {
  isReadingFile.value = true;
  readError.value = '';
  try {
    const { headers, samples } = await readCsvHead(selectedFile.value);
    if (!headers.length) throw new Error('CSV without a header row');

    mappingRows.value = buildMappingRows(headers, samples, attributeKeys.value);
    step.value = 'mapping';
  } catch {
    readError.value = t(
      'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.READ_ERROR'
    );
  } finally {
    isReadingFile.value = false;
  }
};

const handleConfirm = () => {
  if (!isMappingStep.value) {
    if (selectedFile.value) openMapping();
    return;
  }
  if (!canImport.value) return;

  serverErrors.value = [];
  emit('import', {
    file: selectedFile.value,
    labels: selectedLabels.value,
    columnMapping: toColumnMapping(mappingRows.value),
  });
};

const handleBack = () => {
  step.value = 'file';
  serverErrors.value = [];
};

const handleClose = () => {
  handleRemoveFile();
};

// Errors the backend found in the file or the mapping, shown above the mapping.
const setValidationResult = payload => {
  serverErrors.value = payload?.errors || [];
};

const reset = () => {
  handleRemoveFile();
};

defineExpose({ dialogRef, setValidationResult, reset });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isMappingStep
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.TITLE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE')
    "
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CONTINUE')
    "
    :is-loading="isReadingFile"
    :disable-confirm-button="!selectedFile || isReadingFile"
    :width="isMappingStep ? '2xl' : 'xl'"
    :overflow-y-auto="isMappingStep"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <template #titleAddon>
      <a
        :href="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TUTORIAL_URL')"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-sm font-medium text-n-blue-text hover:underline"
      >
        <fluent-icon
          icon="info"
          size="14"
          class="flex-shrink-0 mr-1 text-n-blue-text"
        />
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TUTORIAL') }}
      </a>
    </template>
    <template #description>
      <p v-if="isMappingStep" class="mb-0 text-sm text-n-slate-11">
        {{
          t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.DESCRIPTION')
        }}
      </p>
      <p v-else class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DESCRIPTION') }}
        <a
          :href="SAMPLE_CSV_URL"
          target="_blank"
          rel="noopener noreferrer"
          download="import-contacts-sample.csv"
          class="text-n-blue-text"
        >
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DOWNLOAD_LABEL')
          }}
        </a>
      </p>
    </template>

    <div v-if="isMappingStep" class="flex flex-col gap-4">
      <div
        v-if="serverErrors.length"
        class="flex flex-col gap-1 p-3 rounded-md bg-n-ruby-9/10"
        data-testid="import-server-errors"
      >
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{
            t(
              'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.SERVER_ERRORS'
            )
          }}
        </p>
        <ul class="pl-5 m-0 text-sm list-disc text-n-ruby-11">
          <li
            v-for="error in serverErrors"
            :key="error.code + (error.field || '')"
          >
            {{ error.message }}
          </li>
        </ul>
      </div>
      <p
        v-if="mappingIssues.phoneCount === 0"
        class="m-0 text-sm text-n-ruby-11"
      >
        {{
          t(
            'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.PHONE_REQUIRED'
          )
        }}
      </p>
      <ContactImportColumnMapping
        v-model:rows="mappingRows"
        :contact-attributes="contactAttributes"
        :row-errors="mappingIssues.rowErrors"
        :disabled="isImportingContact"
      />
    </div>

    <div v-else class="flex flex-col gap-4">
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-12 whitespace-nowrap">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABEL') }}
        </label>
        <div class="flex items-center justify-between w-full gap-2">
          <span v-if="selectedFile" class="text-sm text-n-slate-12">
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!selectedFile"
            :label="
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHOOSE_FILE')
            "
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHANGE')"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-12 whitespace-nowrap">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_LABEL') }}
        </label>
        <TagMultiSelectComboBox
          v-model="selectedLabels"
          :options="labelOptions"
          :placeholder="
            t(
              'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_PLACEHOLDER'
            )
          "
          :empty-state="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABELS_EMPTY')
          "
          :disabled="isImportingContact"
        />
      </div>
      <p v-if="readError" class="m-0 text-sm text-n-ruby-11">
        {{ readError }}
      </p>
    </div>
    <input
      ref="fileInput"
      type="file"
      accept="text/csv"
      class="hidden"
      @change="handleFileChange"
    />

    <template v-if="isMappingStep" #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.BACK')
          "
          variant="faded"
          color="slate"
          class="w-full"
          type="button"
          :disabled="isImportingContact"
          @click="handleBack"
        />
        <Button
          :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IMPORT')"
          color="blue"
          class="w-full"
          :is-loading="isImportingContact"
          :disabled="!canImport || isImportingContact"
          type="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
